import CoralMap from '@Conifer-Point/interactive-kinase-map';
import { useState, useRef, useEffect } from 'react';
import { isGroupEntry } from 'BMapsRedux/projectState/entry';
import { EnergySlider } from 'BMapsUI/UIComponents';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { ActivityThresholdInfo } from './AnnotatedSearchResult';

// Default activity cutoffs match ActivityThresholdInfo (the single source of truth) so the coral
// map's initial coloring agrees with the rest of selectivity scoring. bindingScore and enSolute
// are different scales, so each has its own default (enSolute is the gen2 fallback when
// bindingScore is null).
const SCORE_FIELD_INFO = {
    bindingScore: {
        activityName: 'Binding score',
        defaultThreshold: ActivityThresholdInfo.DefaultBindingScoreThreshold,
    },
    enSolute: {
        activityName: 'Interaction energy',
        defaultThreshold: ActivityThresholdInfo.DefaultEnSoluteThreshold,
    },
};

// Curated (reference) panel members are context, not selectivity-scored. Like target/off-target
// nodes, a reference node is colored only when its best pose is active (meets the activity
// threshold); otherwise gray (weak score / no data). The reference color is a colorblind-safe
// amber, kept distinct from the target (blue) and off-target (red) colors.
export const CORAL_REFERENCE_COLOR = '#E69F00';

export function coralRoleLabel(targetInfo) {
    if (isGroupEntry(targetInfo)) return 'reference';
    return targetInfo.isTarget ? 'target' : 'off-target';
}

export function coralNodeColor(targetInfo, isActive) {
    // Gray first: a node (reference included) is only colored when it has a pose meeting the
    // activity threshold.
    if (!isActive) return 'gray';
    if (isGroupEntry(targetInfo)) return CORAL_REFERENCE_COLOR;
    return targetInfo.isTarget ? 'blue' : 'red';
}

// The kinome map draws nodes in a fixed kinase order and can't reorder them, so reference
// (curated panel) nodes are drawn smaller than target/off-target nodes — the scored structures
// then stand out even where a larger reference node would otherwise overlap them.
export const TARGET_NODE_SIZE = 22;
export const REFERENCE_NODE_SIZE = 14;

export function coralNodeSize(targetInfo) {
    return isGroupEntry(targetInfo) ? REFERENCE_NODE_SIZE : TARGET_NODE_SIZE;
}

// Several queried structures can land on the same kinase, but a kinase is one coral node that can
// show only one style. Keep the highest-priority structure: target/off-target beat reference, and
// active beats inactive — so a scored target is never overwritten by a reference panel member that
// shares its kinase.
export function coralNodePriority(targetInfo, isActive) {
    const roleRank = isGroupEntry(targetInfo) ? 1 : 2;
    return roleRank * 10 + (isActive ? 1 : 0);
}

// Each CoralComponent renders a full kinome-map SVG, which is expensive; with ~145
// suggestion rows, rendering them all at once dominated the table's first paint (~13s).
// Defer each row's coral map until it scrolls into view (and keep it once shown), so
// only the handful of on-screen rows pay the cost up front.
export function LazyCoralComponent(props) {
    const [shown, setShown] = useState(false);
    const placeholderRef = useRef(null);
    useEffect(() => {
        if (shown) return undefined;
        const el = placeholderRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setShown(true); // no observer support: just render it
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) setShown(true);
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [shown]);

    // The placeholder reserves the coral map's footprint (so layout is stable) and gives
    // the IntersectionObserver a target to watch until the real map is rendered.
    return (
        <div ref={placeholderRef} style={{ height: '5rem', width: '5rem' }}>
            {shown && <CoralComponent {...props} />}
        </div>
    );
}

export function CoralComponent({ coralMapInfoEntries, primaryScoreField = 'bindingScore' }) {
    const {
        activityName,
        defaultThreshold,
    } = SCORE_FIELD_INFO[primaryScoreField] ?? SCORE_FIELD_INFO.bindingScore;
    const [activityThreshold, setActivityThreshold] = useState(defaultThreshold);
    const styles = {};
    const priorityByKinase = {};
    let lowestScore = Number.MAX_SAFE_INTEGER;
    let highestScore = Number.MIN_SAFE_INTEGER;
    for (const entry of coralMapInfoEntries) {
        const {
            targetInfo, mapCase, kinaseId, fragInfo,
        } = entry;
        const score = entry[primaryScoreField];
        const targetLabel = coralRoleLabel(targetInfo);
        const structureLabel = `${targetLabel} ${kinaseId} (${mapCase.case})`;
        const isActive = score != null && score < activityThreshold;
        let tooltip = `${fragInfo.name} against ${structureLabel}: `;
        if (score != null) {
            tooltip += `${isActive ? 'Active' : 'Non-active'} (${score.toFixed(2)} < ${activityThreshold})`;
            lowestScore = Math.min(lowestScore, score);
            highestScore = Math.max(highestScore, score);
        } else {
            tooltip += 'No fragments found';
        }

        // One node per kinase: keep the highest-priority structure so a target isn't overwritten
        // by a reference panel member that shares its kinase.
        const priority = coralNodePriority(targetInfo, isActive);
        if (priorityByKinase[kinaseId] != null && priority <= priorityByKinase[kinaseId]) continue;
        priorityByKinase[kinaseId] = priority;

        const nodeColor = coralNodeColor(targetInfo, isActive);
        styles[kinaseId] = {
            branchColor: nodeColor,
            nodeColor,
            textColor: 'white',
            nodeSize: coralNodeSize(targetInfo),
            textSize: '12px',
            tooltip,
        };
    }

    // Min is the lowest integer below the lowest score, making it possible to "deactivate" all
    const sliderMin = Math.min(Math.floor(lowestScore-0.1), defaultThreshold);

    return (
        <BMapsTooltip
            noMaxWidth
            title={(
                <div style={{ background: 'white', padding: '1rem', color: 'black' }}>
                    <div style={{ height: '30rem', width: '30rem' }}>
                        <CoralMap
                            TooltipComponent={CoralTooltip}
                            styles={{ ...styles, branchColor: 'lightgray', label: { textColor: 'black' } }}
                        />
                    </div>
                    <p>
                        {activityName}
                        {' range: '}
                        {highestScore.toFixed(1)}
                        {' '}
                        &mdash;
                        {' '}
                        {lowestScore.toFixed(1)}
                    </p>
                    <p>
                        {activityName}
                        {' threshold: '}
                        {activityThreshold.toFixed(1)}
                    </p>
                    <EnergySlider
                        onChange={setActivityThreshold}
                        value={activityThreshold}
                        min={sliderMin}
                        max={0}
                        step={0.5}
                    />
                    <CoralLegend />
                </div>
            )}
            arrow
            placement="right"
        >
            <div style={{ height: '5rem', width: '5rem' }}>
                <CoralMap staticMap styles={{ ...styles, branchColor: 'lightgray' }} />
            </div>
        </BMapsTooltip>
    );
}

export const CoralTooltip = (props) => (
    <BMapsTooltip placement="top" followCursor noMaxWidth {...props} />
);

export const CoralLegend = ({ colorData }) => {
    const legend = [];
    if (!colorData || colorData?.colorScheme === 'target') {
        legend.push({
            color: 'blue',
            label: 'On-target',
        });
        legend.push({
            color: 'red',
            label: 'Off-target',
        });
        legend.push({
            color: CORAL_REFERENCE_COLOR,
            label: 'Reference',
        });
    }

    if (colorData?.colorScheme === 'kinase') {
        colorData.legendStyles.filter((style) => style.kinase).forEach((style) => {
            legend.push({
                color: style.color,
                label: style.kinase,
            });
        });
    }

    if (colorData?.colorScheme === 'compound') {
        colorData.legendStyles.filter((style) => style.compound).forEach((style) => {
            legend.push({
                color: style.color,
                label: style.compound,
            });
        });
    }
    legend.push({
        color: 'gray',
        label: 'Weak score or no data',
    });
    return (
        <div style={{
            display: 'flex', flexDirection: 'row', flexWrap: 'wrap', paddingTop: '0.5em',
        }}
        >
            {/* 0.6875rem is the height used for mui tooltip text. */}
            {legend.map((legendItem) => (
                <div key={legendItem.label} style={{ display: 'flex', flexDirection: 'row' }}>
                    <div style={{
                        width: '0.6875rem',
                        height: '0.6875rem',
                        backgroundColor: legendItem.color,
                        borderRadius: '.5rem',
                        marginRight: '.5rem',
                        marginLeft: '1rem',
                    }}
                    />
                    <p>{legendItem.label}</p>
                </div>
            ))}
        </div>
    );
};
